import { useEffect, useState } from "react";
import axios from "axios";
import { environment } from "../../config/env";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "../ui/carousel";
import { Badge } from "../ui/badge";
import {
  Bed,
  Wifi,
  Tv,
  Wind,
  Coffee,
  Image as ImageIcon,
  Fan,
  Bath,
  ShowerHead,
  GlassWater,
  Sun,
  Phone,
  Sparkles,
  Shirt,
  CheckCircle2,
  Maximize2,
  Users,
} from "lucide-react";

interface RoomAmenity {
  description: string;
  icon?: string;
  translations?: Record<string, string>;
}

interface RoomTranslation {
  description?: string;
  note?: string;
}

interface RoomDetail {
  code: string;
  description: string;
  note?: string;
  size?: string;
  minPax?: number;
  maxPax?: number;
  images: string[];
  amenities?: RoomAmenity[];
  translations?: Record<string, RoomTranslation>;
}

function getAmenityLucideIcon(iconName?: string, desc?: string) {
  const icon = (iconName || "").toLowerCase();
  const text = (desc || "").toLowerCase();

  if (
    icon.includes("wifi") ||
    text.includes("wifi") ||
    text.includes("wi-fi") ||
    text.includes("internet")
  ) {
    return <Wifi className="h-4 w-4 text-emerald-600 shrink-0" />;
  }
  if (icon.includes("tv") || text.includes("tv") || text.includes("televis")) {
    return <Tv className="h-4 w-4 text-emerald-600 shrink-0" />;
  }
  if (
    icon.includes("snowflake") ||
    text.includes("aria condizionata") ||
    text.includes("ac") ||
    text.includes("climatiz")
  ) {
    return <Wind className="h-4 w-4 text-emerald-600 shrink-0" />;
  }
  if (icon.includes("fan") || text.includes("ventilator")) {
    return <Fan className="h-4 w-4 text-emerald-600 shrink-0" />;
  }
  if (icon.includes("shower") || text.includes("doccia")) {
    return <ShowerHead className="h-4 w-4 text-emerald-600 shrink-0" />;
  }
  if (icon.includes("bath") || text.includes("bagno")) {
    return <Bath className="h-4 w-4 text-emerald-600 shrink-0" />;
  }
  if (
    icon.includes("glass") ||
    icon.includes("cheers") ||
    text.includes("minibar") ||
    text.includes("frigo")
  ) {
    return <GlassWater className="h-4 w-4 text-emerald-600 shrink-0" />;
  }
  if (
    icon.includes("store") ||
    icon.includes("balcon") ||
    text.includes("balcon") ||
    text.includes("finestra") ||
    text.includes("vista")
  ) {
    return <Sun className="h-4 w-4 text-emerald-600 shrink-0" />;
  }
  if (icon.includes("phone") || text.includes("telefon")) {
    return <Phone className="h-4 w-4 text-emerald-600 shrink-0" />;
  }
  if (
    text.includes("asciugacapelli") ||
    text.includes("phon") ||
    text.includes("hair")
  ) {
    return <Wind className="h-4 w-4 text-emerald-600 shrink-0" />;
  }
  if (
    text.includes("armadio") ||
    text.includes("guardaroba") ||
    text.includes("wardrobe")
  ) {
    return <Shirt className="h-4 w-4 text-emerald-600 shrink-0" />;
  }
  if (
    text.includes("asciugamani") ||
    text.includes("towels") ||
    text.includes("sanific") ||
    text.includes("prodotti") ||
    text.includes("amenities") ||
    icon.includes("water") ||
    icon.includes("hand")
  ) {
    return <Sparkles className="h-4 w-4 text-emerald-600 shrink-0" />;
  }

  return <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />;
}

function RoomDetailInnerContent({
  detail,
  roomCode,
}: {
  detail: RoomDetail | null;
  roomCode: string;
}) {
  const { i18n } = useTranslation();
  const currentLang = i18n?.language?.split("-")[0]?.toLowerCase() || "it";

  const description =
    (currentLang !== "it" &&
      detail?.translations?.[currentLang]?.description) ||
    detail?.description ||
    `Sistemazione di tipologia ${roomCode} dotata di tutti i comfort necessari per garantire un soggiorno piacevole e rilassante.`;

  const note =
    (currentLang !== "it" && detail?.translations?.[currentLang]?.note) ||
    detail?.note;

  const hasAmenities = detail?.amenities && detail.amenities.length > 0;

  return (
    <div className="space-y-6 pt-2">
      {/* Gallery Carousel */}
      {detail && detail.images && detail.images.length > 0 ? (
        <div className="px-6 relative">
          <Carousel
            autoPlay={true}
            opts={{ loop: true }}
            className="w-full rounded-xl overflow-hidden shadow-sm"
          >
            <CarouselContent>
              {detail.images.map((img, i) => (
                <CarouselItem key={i}>
                  <div className="aspect-video w-full rounded-xl overflow-hidden bg-zinc-100 dark:bg-zinc-800 relative">
                    <img
                      src={img}
                      alt={`${description} - Foto ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            {detail.images.length > 1 && (
              <>
                <CarouselPrevious className="left-2" />
                <CarouselNext className="right-2" />
              </>
            )}
          </Carousel>
        </div>
      ) : (
        <div className="rounded-xl border bg-muted/20 p-8 flex flex-col items-center justify-center text-center gap-3">
          <div className="h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-950/50 flex items-center gap-1 justify-center text-emerald-600">
            <ImageIcon className="h-6 w-6" />
          </div>
          <p className="text-sm font-semibold">{description}</p>
          <p className="text-xs text-muted-foreground">
            Galleria foto camera in aggiornamento
          </p>
        </div>
      )}

      {/* Overview Badges: Pax & Size */}
      {(detail?.size || detail?.maxPax) && (
        <div className="flex items-center gap-2 flex-wrap px-1">
          {detail?.size && (
            <Badge
              variant="secondary"
              className="gap-1.5 py-1 px-3 text-xs font-medium"
            >
              <Maximize2 className="h-3.5 w-3.5 text-emerald-600" />
              {detail.size.includes("m") ? detail.size : `${detail.size} m²`}
            </Badge>
          )}
          {detail?.maxPax && (
            <Badge
              variant="secondary"
              className="gap-1.5 py-1 px-3 text-xs font-medium"
            >
              <Users className="h-3.5 w-3.5 text-emerald-600" />
              {detail.minPax && detail.minPax !== detail.maxPax
                ? `${detail.minPax} - ${detail.maxPax} ospiti`
                : `Fino a ${detail.maxPax} ${detail.maxPax === 1 ? "ospite" : "ospiti"}`}
            </Badge>
          )}
        </div>
      )}

      {/* Description Card */}
      <div className="rounded-xl border bg-card p-5 space-y-3">
        <h4 className="font-semibold text-sm text-foreground">
          Descrizione della sistemazione
        </h4>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {description}
        </p>

        {note && (
          <div
            className="text-xs text-muted-foreground pt-2 border-t border-border/50 leading-relaxed space-y-1"
            dangerouslySetInnerHTML={{ __html: note }}
          />
        )}
      </div>

      {/* Services / Comfort Badges */}
      <div className="rounded-xl border bg-card p-5 space-y-3">
        <h4 className="font-semibold text-sm text-foreground">
          Dotazioni & Servizi Camera
        </h4>
        {hasAmenities ? (
          <div className="grid grid-cols-2 gap-2.5 text-xs text-muted-foreground">
            {detail!.amenities!.map((amenity, idx) => {
              const amenityLabel =
                (currentLang !== "it" && amenity.translations?.[currentLang]) ||
                amenity.description;

              return (
                <div
                  key={idx}
                  className="flex items-center gap-2.5 rounded-lg border p-2.5 bg-muted/10 transition-colors hover:bg-muted/20"
                >
                  {getAmenityLucideIcon(amenity.icon, amenity.description)}
                  <span
                    className="font-medium text-foreground/90 truncate"
                    title={amenityLabel}
                  >
                    {amenityLabel}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-2 rounded-lg border p-2.5 bg-muted/10">
              <Wifi className="h-4 w-4 text-emerald-600 shrink-0" />
              <span>Wi-Fi Gratuito</span>
            </div>
            <div className="flex items-center gap-2 rounded-lg border p-2.5 bg-muted/10">
              <Wind className="h-4 w-4 text-emerald-600 shrink-0" />
              <span>Aria Condizionata</span>
            </div>
            <div className="flex items-center gap-2 rounded-lg border p-2.5 bg-muted/10">
              <Tv className="h-4 w-4 text-emerald-600 shrink-0" />
              <span>TV Schermo Piatto</span>
            </div>
            <div className="flex items-center gap-2 rounded-lg border p-2.5 bg-muted/10">
              <Coffee className="h-4 w-4 text-emerald-600 shrink-0" />
              <span>Set di Cortesia</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function RoomDetailModal({
  open,
  onOpenChange,
  roomCode,
  estimateId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  roomCode: string;
  estimateId: number;
}) {
  const [detail, setDetail] = useState<RoomDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);

  useEffect(() => {
    if (!open || !roomCode || !estimateId) return;
    setLoading(true);
    axios.defaults.baseURL = environment.ELITE_BASE_URI;
    axios
      .get(`/Estimate/room-detail/${estimateId}/${roomCode}`)
      .then((res) => setDetail(res.data))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [open, roomCode, estimateId]);

  useEffect(() => {
    const mq = window.matchMedia("(orientation: portrait)");
    setIsPortrait(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsPortrait(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const headerTitle = (
    <div className="flex items-center gap-2 text-xl font-bold">
      <Bed className="h-5 w-5 text-emerald-600" />
      {detail?.description || roomCode}
      {roomCode && (
        <Badge
          variant="outline"
          className="ml-2 font-mono text-xs border-emerald-500/40 text-emerald-700 dark:text-emerald-400"
        >
          {roomCode}
        </Badge>
      )}
    </div>
  );

  const loaderContent = (
    <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
      <div className="h-5 w-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      <span>Caricamento dettagli camera...</span>
    </div>
  );

  if (isPortrait) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="max-h-[88vh] overflow-y-auto rounded-t-2xl space-y-4"
        >
          <SheetHeader>
            <SheetTitle>{headerTitle}</SheetTitle>
          </SheetHeader>
          {loading ? (
            loaderContent
          ) : (
            <RoomDetailInnerContent detail={detail} roomCode={roomCode} />
          )}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto space-y-4 rounded-2xl">
        <DialogHeader>
          <DialogTitle>{headerTitle}</DialogTitle>
        </DialogHeader>
        {loading ? (
          loaderContent
        ) : (
          <RoomDetailInnerContent detail={detail} roomCode={roomCode} />
        )}
      </DialogContent>
    </Dialog>
  );
}
